import { Activity, useCallback, useMemo, useRef, useState } from 'react';

import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import CustomSelect from '@ui/select/custom-select/custom-select';
import Container from '@ui/container/container';
import BrandliveAvatar from '@ui/brandlive-avatar/brandlive-avatar';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import { useAppDispatch } from '@/store/hooks';
import { BLAdmin } from '@/types/profile.types';
import useSelectSessionCrew from '../../helpers/use-select-session-crew';
import useSelectChannelCrew from '../../helpers/use-select-channel-crew';
import { ButtonProps } from '@ui/buttons/button/button.types';
import useParamsOrFirstSessionUuid from '@/utils/use-params-or-first-session-uuid';
import { updateSessionCrewAssociations } from '@/store/slices/project';
import { ISessionCrew } from '@/types/session.types';

const AddCrewModal = ({ buttonSize = 'medium' }: { buttonSize?: ButtonProps['designSize'] }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [open, setOpen] = useState(false);
  const [isSelectFocused, setIsSelectFocused] = useState(false);
  const [newlySelectedOptions, setNewlySelectedOptions] = useState<TReactSelectOption[]>([]);

  const dispatch = useAppDispatch();

  const sessionUuid = useParamsOrFirstSessionUuid();

  const sessionCrew = useSelectSessionCrew();

  const allChannelCrew = useSelectChannelCrew();

  const allProducerOptions = useMemo(() => {
    return allChannelCrew.map((admin) => ({
      label: `${admin.profile.first_name || ''} ${admin.profile.first_name || ''} ${admin.email || ''}`.trim(),
      value: admin.id,
      meta: admin,
    }));
  }, [allChannelCrew]);

  const { defaultOptionsMap } = useMemo(() => {
    const _defaultOptionsMap: Record<number, TReactSelectOption<string, number, ISessionCrew>> = {};
    const _defaultOptions = sessionCrew.reduce(
      (allMembers: TReactSelectOption<string, number, ISessionCrew>[], member: ISessionCrew) => {
        if (!member?.bl_admin?.id) return allMembers;
        const memberOption = {
          label:
            `${member?.bl_admin?.profile.first_name || ''} ${member?.bl_admin?.profile.last_name || ''} ${member?.bl_admin?.email}`.trim(),
          value: member.bl_admin?.id,
          meta: member,
        };
        _defaultOptionsMap[member.bl_admin.id] = memberOption;
        return [...allMembers, memberOption];
      },
      []
    );

    return {
      defaultOptions: _defaultOptions,
      defaultOptionsMap: _defaultOptionsMap,
    };
  }, [sessionCrew]);

  const availableProducerOptions = useMemo(() => {
    return allProducerOptions.filter((option) => !defaultOptionsMap[option.value]);
  }, [allProducerOptions, defaultOptionsMap]);

  const handleClose = useCallback(
    (options?: { forceClose?: boolean }) => {
      if (isSelectFocused && !options?.forceClose) return;
      setOpen(false);
    },
    [isSelectFocused]
  );

  const handleSave = useCallback(async () => {
    if (!sessionUuid) return;

    dispatch(
      updateSessionCrewAssociations({
        sessionUuid,
        addedCrew: newlySelectedOptions.map((option) => option.meta as BLAdmin),
      })
    );

    handleClose({ forceClose: true });
  }, [dispatch, handleClose, newlySelectedOptions, sessionUuid]);

  const handleTransitionOpened = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleTransitionClosed = useCallback(() => {
    setNewlySelectedOptions([]);
  }, []);

  return (
    <>
      <Button
        designSize={buttonSize}
        onClick={() => {
          setOpen(true);
        }}
        backgroundColor={getThemeColorCSSVariable()}
        hoverBackgroundColor={getThemeColorsCssMixVariable()}
      >
        <Text className="L2 flex ai-center gap-8" color={getThemeColorContrastCSSVariable()}>
          <Icon name={ICONS.ADD} size={12} /> <span>Add</span>
        </Text>
      </Button>

      <Modal
        design="GlobalCont1"
        customSize={{ height: 'fit-content', minHeight: '192px', width: '640px', maxWidth: '90vw' }}
        open={open}
        onRequestClose={handleClose}
        onTransitionOpened={handleTransitionOpened}
        onTransitionClosed={handleTransitionClosed}
        renderHeaderContent={() => (
          <Container height="96px" padding="24px 24px 16px 40px" className="flex-column gap-4">
            <Container className="flex ai-center jc-space-between">
              <Text design="GlobalIcoText1" className="H4" tag="h4">
                Add crew members
              </Text>
              {/* this button bypasses the handleCloseModal conditional check */}
              <Button
                designSize="small"
                design="LocalBttnSec"
                className="radius-full square-32 flex-column ai-center jc-center"
                onClick={() => handleClose({ forceClose: true })}
              >
                <Icon name={ICONS.CLOSE} size={10} />
              </Button>
            </Container>
            <Text className="S3" design="GlobalIcoText2">
              Add crew members to your project
            </Text>
          </Container>
        )}
      >
        <Container className="flex ai-center jc-center gap-12" padding="16px 40px 40px 40px">
          <Container width="100%" maxWidth="450px" className="flex-1">
            <CustomSelect
              id="add-crew-modal-select"
              hideHeader
              inputRef={inputRef}
              placeholder="Add crew members"
              maskOverflowColor="var(--global-cont-1)"
              options={availableProducerOptions}
              controlledOptions={newlySelectedOptions}
              onFocusChange={(isFocused) => {
                // slight delay to allow the modal click handler to trigger before flipping
                // so we get an accurate read at the time of click
                setTimeout(() => {
                  setIsSelectFocused(isFocused);
                }, 100);
              }}
              onChange={setNewlySelectedOptions}
              // filterSelected={false}
              RenderMenuOptionContent={({ option, isSelected }) => {
                const member = option.meta as BLAdmin | null;
                if (!member) return;
                return (
                  <div className="flex ai-center jc-flex-start gap-8 width-100-percent">
                    <BrandliveAvatar
                      profile={{
                        first_name: member?.profile?.first_name || '',
                        last_name: member?.profile?.last_name || '',
                        email: member?.email,
                        picture: member?.profile?.picture,
                      }}
                    />{' '}
                    <Text className="S3 flex ai-center jc-flex-start gap-8 fw-nowrap">
                      <Text clamp={1} whiteSpace="normal" wordBreak="break-all" design="GlobalIcoText1">
                        {`${member?.profile?.first_name || ''} ${member?.profile?.last_name || ''}`.trim()}
                      </Text>
                      <Activity mode={member?.email ? 'visible' : 'hidden'}>
                        <Text
                          design="GlobalIcoText3"
                          className="C1"
                          clamp={1}
                          whiteSpace="normal"
                          wordBreak="break-all"
                        >
                          {member?.email || ''}
                        </Text>
                      </Activity>
                    </Text>
                    <Activity mode={isSelected ? 'visible' : 'hidden'}>
                      <Text design="GlobalIcoText1" className="C2 margin-l-auto margin-r-0" whiteSpace="nowrap">
                        Already added
                      </Text>
                    </Activity>
                  </div>
                );
              }}
            />
          </Container>

          <Button
            backgroundColor={getThemeColorCSSVariable()}
            hoverBackgroundColor={getThemeColorsCssMixVariable()}
            disabledOpacity={0.7}
            designSize="medium"
            onClick={handleSave}
            disabled={!newlySelectedOptions.length}
          >
            <Text className="flex ai-center gap-8" color={getThemeColorContrastCSSVariable()}>
              <Icon name={ICONS.SEND_3} size={12} />
              <Text whiteSpace="nowrap" className="L1">
                Add
              </Text>
            </Text>
          </Button>
        </Container>
      </Modal>
    </>
  );
};

export default AddCrewModal;
